import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Edit2, Trash2, Plus, Search, Building2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';

function PropertyTypesList() {
  const [propertyTypes, setPropertyTypes] = useState<any[]>([]);
  const [propertyCounts, setPropertyCounts] = useState<{[key: string]: number}>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPropertyTypes();
  }, []);

  const fetchPropertyTypes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('property_types')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) {
        console.error('Error fetching property types:', error);
        toast.error('Errore nel caricamento delle tipologie immobiliari');
      } else {
        setPropertyTypes(data || []);
      }
      
      // Fetch count of properties for each type
      const counts: {[key: string]: number} = {};
      
      // Get all properties with their type_id
      const { data: properties, error: propertiesError } = await supabase
        .from('properties')
        .select('type_id');
        
      if (propertiesError) {
        console.error('Error fetching property counts:', propertiesError);
      } else if (properties) {
        // Count properties by type_id
        properties.forEach(property => {
          if (property.type_id) {
            counts[property.type_id] = (counts[property.type_id] || 0) + 1;
          }
        });
      }
      
      setPropertyCounts(counts);
    } catch (error) {
      console.error('Error fetching property types:', error);
      toast.error('Errore nel caricamento delle tipologie immobiliari');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa tipologia immobiliare?')) return;
    
    try {
      // Check if the property type is being used in any property
      const { count, error: countError } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('type_id', id);
      
      if (countError) {
        throw countError;
      }
      
      if (count && count > 0) {
        toast.warning(`Questa tipologia è utilizzata in ${count} immobili. Rimuovi prima l'associazione.`);
        return;
      }
      
      const { error } = await supabase
        .from('property_types')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      toast.success('Tipologia immobiliare eliminata con successo');
      setPropertyTypes(propertyTypes.filter(type => type.id !== id));
    } catch (error: any) {
      console.error('Error deleting property type:', error);
      toast.error(`Errore: ${error.message || 'Si è verificato un errore durante l\'eliminazione'}`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const filteredPropertyTypes = searchQuery
    ? propertyTypes.filter(type => 
        type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (type.code && type.code.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : propertyTypes;

  return (
    <AdminLayout title="Gestione Tipologie Immobiliari">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-semibold">Tipologie Immobiliari</h2>
        <Link
          to="/admin/property-types/new"
          className="bg-primary hover:bg-primary-light text-white px-4 py-2 rounded-md transition-colors flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuova Tipologia
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Cerca tipologia..."
            className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento tipologie immobiliari...</p>
        </div>
      ) : filteredPropertyTypes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna tipologia immobiliare trovata</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery 
              ? 'Nessuna tipologia corrisponde alla tua ricerca.' 
              : 'Non ci sono ancora tipologie immobiliari. Crea la tua prima tipologia.'}
          </p>
          <Link
            to="/admin/property-types/new"
            className="inline-flex items-center bg-primary hover:bg-primary-light text-white px-4 py-2 rounded-md transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuova Tipologia
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-3 px-4 md:px-6 font-semibold text-gray-700 whitespace-nowrap">Nome</th>
                <th className="text-left py-3 px-4 md:px-6 font-semibold text-gray-700 whitespace-nowrap">Codice</th>
                <th className="text-left py-3 px-4 md:px-6 font-semibold text-gray-700 whitespace-nowrap">Immobili Associati</th>
                <th className="text-right py-3 px-4 md:px-6 font-semibold text-gray-700 whitespace-nowrap">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredPropertyTypes.map((type) => (
                <tr key={type.id} className="border-t hover:bg-gray-50">
                  <td className="py-4 px-6">
                    <div className="font-medium">{type.name}</div>
                  </td>
                  <td className="py-4 px-6">
                    {type.code ? (
                      <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium">
                        {type.code}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                      {propertyCounts[type.id] || 0} immobili
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex justify-end space-x-2">
                      <Link
                        to={`/admin/property-types/${type.id}/edit`}
                        className="text-blue-600 hover:text-blue-700"
                        title="Modifica"
                      >
                        <Edit2 className="w-5 h-5" />
                      </Link>
                      <button
                        onClick={() => handleDelete(type.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Elimina"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default PropertyTypesList;